import * as React from 'react';
import styled from '@independent-software/typeui/styles/Theme'

import { Flex } from '@independent-software/typeui/controls/Flex';
import { Tabs } from '@independent-software/typeui/controls/Tabs';
import { Dropdown } from '@independent-software/typeui/controls/Dropdown';
import { Input } from '@independent-software/typeui/controls/Input';
import { Form } from '@independent-software/typeui/controls/Form';

import { AuthContext } from '../../AuthContext';
import { Parameter, Parametercategory, ParametercategoryFactory } from '../../resource/';
import { Query } from '../../services/';
import { Section } from '../../modules';
import { CategoryTree } from '../../controls/CategoryTree';

interface IProps {
  /** Initial form data. */
  data: Parameter;
  /** Called whenever form changes. */
  onChange: (data: Parameter, forceupdate: boolean) => void;
  /** Called whenever a field validates. Returns validation state for whole form. */
  onValidate: (valid: boolean) => void;
  /** 
   * Mark form as dirty. If dirty, it will show error messages for all fields,
   * even pristine fields.
   */  
  dirty?: boolean;
}

const ParameterForm = (props: IProps) => {
  const auth = React.useContext(AuthContext);
  const [parametercategories, setParametercategories] = React.useState([] as Parametercategory[]);

  React.useEffect(() => {
    // Retrieve a list of parametercategories:
    const query = new Query('name', 'asc');
    ParametercategoryFactory.getSome(auth, 0, 999, query)
    .then((res) => setParametercategories(res.items));    
  }, []);

  const handleCategoryClick = (cat: Parametercategory) => {
    let idx = props.data.parametercategories.findIndex(mycat => mycat.id == cat.id);
    if(idx == -1) {
      props.data.parametercategories.push(cat);
    } else {
      props.data.parametercategories.splice(idx, 1);
    }
    props.onChange(props.data, true);
  }

  const renderName = () =>
    <Form.Field 
      name="name" label="Name"
      value={props.data.name}
      control={(<Input type="text" placeholder="Name" fluid/>)}
      required={{message: "A parameter name is required."}}
      minLength={{length: 3, message: "Parameter name must be at least 3 characters."}}
      maxLength={{length: 255, message: "Parameter name may not be longer than 255 characters."}}
      hint={(<React.Fragment>Please enter a parameter name, e.g. <code>Current speed</code>.</React.Fragment>)}/>     

  const renderUnit = () =>
    <Form.Field 
      name="unit" label="Unit"
      value={props.data.unit}
      control={(<Input type="text" placeholder="Unit" fluid/>)}
      required={{message: "A parameter unit is required."}}
      maxLength={{length: 255, message: "Parameter unit may not be longer than 255 characters."}}
      hint={(<React.Fragment>Please enter a parameter unit, e.g. <code>m/s</code>.</React.Fragment>)}/>           

  const renderMaxvalue = () =>
    <Form.Field 
      name="maxvalue" label="Maximum value"
      value={props.data.maxvalue}
      control={(<Input type="text" placeholder="Maximum value" fluid/>)}
      required={{message: "A parameter maximum allowable value is required."}}
      isFloat={{message: "Maximum allowable value must be numeric."}}
      hint={(<React.Fragment>Please enter a maximum allowable value.</React.Fragment>)}/>

  const renderMinvalue = () =>
    <Form.Field 
      name="minvalue" label="Minimum value"
      value={props.data.minvalue}
      control={(<Input type="text" placeholder="Minimum value" fluid/>)}
      required={{message: "A parameter minimum allowable value is required."}}
      isFloat={{message: "Minimum allowable value must be numeric."}}
      hint={(<React.Fragment>Please enter a minimum allowable value.</React.Fragment>)}/>

  const renderRecommendedHigh = () =>
    <Form.Field 
      name="recommended_high" label="Recommended high value"
      value={props.data.recommended_high}
      control={(<Input type="text" placeholder="Recommended high value" fluid/>)}
      required={{message: "A recommended allowable high value is required."}}
      isFloat={{message: "Recommended high value must be numeric."}}
      hint={(<React.Fragment>Please enter a recommended allowable high value.</React.Fragment>)}/>
  
  const renderRecommendedLow = () =>
    <Form.Field 
      name="recommended_low" label="Recommended low value"
      value={props.data.recommended_low}
      control={(<Input type="text" placeholder="Recommended low value" fluid/>)}
      required={{message: "A recommended allowable low value is required."}}
      isFloat={{message: "Recommended low value must be numeric."}}
      hint={(<React.Fragment>Please enter a recommended allowable low value.</React.Fragment>)}/>

  const renderDecimals = () => 
    <Form.Field 
      name="decimals" label="Decimals"
      value={props.data.decimals}
      control={(<Input type="text" placeholder="Number of decimals" fluid/>)}
      required={{message: "Number of decimals is required."}}
      isInt={{message: "Number of decimals must be a whole number."}}
      min={{value: 0, message: "Number of decimals must be 0 or greater."}}
      max={{value: 10, message: "Number of decimals must be 10 or less."}}
      hint={(<React.Fragment>Please enter the number of decimals to display.</React.Fragment>)}/>     

  const renderType = () =>
    <Form.Field 
      name="type" label="Type"
      value={props.data.type}
      control={<Dropdown placeholder="Type" fluid data={["primary", "derived"]}
        label={(item:string) => item.charAt(0).toUpperCase() + item.slice(1)}>
        <Dropdown.Column>{(item:string) => item.charAt(0).toUpperCase() + item.slice(1)}</Dropdown.Column>
      </Dropdown>}
      hint={(<React.Fragment>Please select the parameter type.</React.Fragment>)}/>          

  const renderFormula = () =>
    <Form.Field 
      name="formula" label="Formula"
      value={props.data.formula}
      control={(<Input type="text" placeholder="Formula" fluid/>)}
      maxLength={{length: 255, message: "Parameter formula may not be longer than 255 characters."}}
      hint={(<React.Fragment>Please enter a parameter formula.</React.Fragment>)}/>
            
  return (
    <Form
      data={props.data} dirty={props.dirty} onChange={props.onChange} onValidate={props.onValidate}>
      <Tabs underlined>
        <Tabs.Pane label="General">
          <Section padded>
            <Flex.Columns count={2}>
              <>
                {renderName()}
                {renderUnit()}
                {renderDecimals()}
              </>
              <>
                {renderType()}
                {renderFormula()}
                <Form.Uncontrolled label="Categories">
                  <TreeContainer>
                    <CategoryTree 
                      items={parametercategories} 
                      active={props.data.parametercategories.map(sc => sc.id)}
                      onClick={handleCategoryClick}/>
                  </TreeContainer>
                </Form.Uncontrolled>                  
              </>
            </Flex.Columns>
          </Section>
        </Tabs.Pane>
        <Tabs.Pane label="Limits">
          <Section padded>
            <Flex.Columns count={2}>
              <>
                {renderMinvalue()}
                {renderMaxvalue()}
              </>
              <>
                {renderRecommendedLow()}
                {renderRecommendedHigh()}
              </>
            </Flex.Columns>
          </Section>
        </Tabs.Pane>          
      </Tabs>
    </Form>
  )
}

const TreeContainer = styled.div`
  width: 100%;
  height: 280px;
  overflow-y: scroll;
  border: solid 1px #E0E1E2;
`

export { ParameterForm }
